/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";

import { useTranslation } from "react-i18next";

import { WarningIcon } from "@dotnet/telematics-design-system";

import {
  COMPANY_CARD_STATUS,
  compareVersions,
  DATE_FORMATS,
  ICON_DEFAULT_STYLE,
  useDateTime
} from "../../../../../../../../../shared";
import ExpiryBadge from "../../../../../../../../../shared/components/badges/ExpiryBadge";
import Badge from "../../../../../../../../Badge/Badge";
import InfoIconWithTooltip from "../../../../../../../../IconsWithTooltips/InfoIconWithTooltip/InfoIconWithTooltip";
import { useStyles } from "../../../../../../../styles";
import { CompanyCardDto } from "../../../../../../../types";
import { useCompanyCardDetails } from "../../../../../hooks";
import DetailsCardItemText from "../../../../DetailsCardItemText";
import DetailsCardItemTitle from "../../../../DetailsCardItemTitle";
import CardStatusBadge from "../../CardStatusBadge/CardStatusBadge";

interface CompanyCardContentProps {
  cardData: CompanyCardDto;
}

const CompanyCardContent = ({ cardData }: CompanyCardContentProps) => {
  const { t } = useTranslation();

  const { getExpiryLabel, getCompanyCardExpiryStatus } =
    useCompanyCardDetails();

  const { formattedDate, currentDateFormatWithoutTime } = useDateTime();

  const { paperCardItem, paperCardItemAlignStart } = useStyles();

  const validUntilWithoutTime = formattedDate(
    cardData.validUntil,
    DATE_FORMATS.YearMonthDay
  );

  const hasVersionData = cardData?.ccaVersion && cardData?.latestVersion;

  const isLatestCCAVersion =
    hasVersionData &&
    compareVersions(cardData?.ccaVersion, cardData?.latestVersion) === 0;

  const hasValidUntil = validUntilWithoutTime && validUntilWithoutTime !== "-";

  const cardExpiryDate = formattedDate(
    cardData.validUntil,
    currentDateFormatWithoutTime
  ) as string;

  const expiredCardCCAText = cardData?.status ===
    COMPANY_CARD_STATUS.Expired && (
    <DetailsCardItemText textToRender={cardData.ccaVersion} />
  );
  const inactiveCardCCAInfo = cardData?.status ===
    COMPANY_CARD_STATUS.Inactive && (
    <InfoIconWithTooltip
      iconTestId="cca-version-info-icon"
      title={t(
        "Company cards##version details after connection is established"
      )}
    />
  );

  const expiryInfo = hasValidUntil ? (
    <ExpiryBadge
      data-testid="card-expiry-badge"
      color={getCompanyCardExpiryStatus(validUntilWithoutTime)}
      date={cardExpiryDate}
    >
      {getExpiryLabel(validUntilWithoutTime)}
    </ExpiryBadge>
  ) : (
    <InfoIconWithTooltip
      iconTestId="card-expiry-info-icon"
      title={t("Company cards##expiry tooltip")}
    />
  );

  const warningIconBadge = (
    <span css={css({ position: "relative" })}>
      <Badge
        color="warning"
        tooltipText={t("Company cards##update version warning")}
        tooltipSize="medium"
        customStyle={{ position: "absolute", top: -3 }}
      >
        <WarningIcon style={ICON_DEFAULT_STYLE} />
      </Badge>
    </span>
  );

  const ccaVersionInfo = cardData.ccaVersion ? (
    <DetailsCardItemText
      testId="card-latest-version-text"
      textToRender={cardData.ccaVersion}
      titleInlineAddition={
        isLatestCCAVersion
          ? `(${t("Company cards##latest version")})`
          : warningIconBadge
      }
    />
  ) : (
    expiredCardCCAText || inactiveCardCCAInfo
  );

  const cardNumberInfo = cardData.cardNumber ? (
    <DetailsCardItemText
      testId="card-number-text"
      textToRender={cardData.cardNumber}
    />
  ) : (
    <InfoIconWithTooltip
      iconTestId="card-number-info-icon"
      title={t("Company cards##card number tooltip")}
    />
  );

  return (
    <>
      <div css={css(paperCardItem)}>
        <DetailsCardItemTitle
          title={t("Company cards##status")}
          customLineHeight="24px"
        />

        <CardStatusBadge card={cardData} tooltipSize="medium" />
      </div>
      <div css={css(paperCardItem)}>
        <DetailsCardItemTitle title={t("Company cards##expiry")} />
        {expiryInfo}
      </div>
      <div css={css(paperCardItem)}>
        <DetailsCardItemTitle title={t("Company cards##card number")} />
        {cardNumberInfo}
      </div>
      <div css={css([paperCardItem, paperCardItemAlignStart])}>
        <DetailsCardItemTitle title={t("Company cards##card id")} />
        <DetailsCardItemText
          textToRender={cardData.companyCardId}
          copiedValue={cardData.companyCardId}
          customStyle={{ lineBreak: "anywhere" }}
        />
      </div>
      <div css={css(paperCardItem)}>
        <DetailsCardItemTitle title={t("Company cards##cca version")} />
        {ccaVersionInfo}
      </div>
    </>
  );
};

export default CompanyCardContent;
